import { AssetType } from '@compass/client/types/asset';
import { Action } from '@compass/client/types/case_record';
import type { Email } from '@compass/client/types/email';
import { GenericTaskStatus, GenericTaskType } from '@compass/client/types/generic_task';
import { TemplateTargetType } from '@compass/client/types/template';
import { AlertCircle, CheckCircle2, Edit2, Globe, HelpCircle, Loader2 } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { FormattedDateRelative } from '@/components/FormattedDateRelative';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import { EditableField } from '@/components/ui/editable-field';
import { PqcDecision } from '@/constants/caseStates';
import { useCaseRecordAssets } from '@/hooks/useCaseRecordAssets';
import { useExecuteAction } from '@/hooks/useCases';
import { useCaseRecordEmails } from '@/hooks/useEmails';
import { useGenericTasks } from '@/hooks/useGenericTask';
import { useTemplates } from '@/hooks/useTemplates';
import type { EditableFieldState } from '@/types/editableField';
import type { FieldGroup, FormField } from '@/types/formSchema';
import { cn } from '@/utils';
import { ackFieldPlaceholder, PORTAL_CONFIRMATION_ASSET_BASE } from '@/utils/ackPlaceholder';
import { buildGoTemplatePattern } from '@/utils/template';

const MANUFACTURER_TEMPLATE_SLUG = 'pqc-manufacturer-email';

/**
 * From a case's emails, find the latest delivered manufacturer outbound email
 * (matching the pqc-manufacturer-email template subject) and the latest inbound
 * reply (linked via parent_id).
 */
function useManufacturerEmails(caseId: string) {
  const { data: emailsData, isLoading: emailsLoading } = useCaseRecordEmails(caseId);
  // No programId/workflowId — template is org-scoped (program_id=null).
  const { data: templatesData, isLoading: templateLoading } = useTemplates({
    slug: MANUFACTURER_TEMPLATE_SLUG,
    targetType: TemplateTargetType.TEMPLATE_TARGET_TYPE_EMAIL,
  });

  const { manufacturerEmail, replyEmail } = useMemo(() => {
    const emails = emailsData?.emails;
    const subject = templatesData?.templates?.[0]?.target?.email?.subject;
    if (!emails || !subject) return { manufacturerEmail: null, replyEmail: null };

    const pattern = buildGoTemplatePattern(subject);

    // Emails are sorted oldest-first. Walk forward to find the latest matching outbound.
    let mfgEmail: Email | null = null;
    for (const email of emails) {
      if (email.direction === 'outbound' && pattern.test(email.subject ?? '')) {
        mfgEmail = email;
      }
    }

    // Find the latest inbound reply linked to the manufacturer email via parent_id.
    let reply: Email | null = null;
    if (mfgEmail?.id) {
      for (const email of emails) {
        if (email.direction === 'inbound' && email.parent_id === mfgEmail!.id) {
          reply = email;
        }
      }
    }

    return { manufacturerEmail: mfgEmail, replyEmail: reply };
  }, [emailsData, templatesData]);

  return { manufacturerEmail, replyEmail, isLoading: emailsLoading || templateLoading };
}

const SectionHeader: React.FC<{
  allFilled: boolean;
  filledCount: number;
  totalCount: number;
}> = ({ allFilled, filledCount, totalCount }) => (
  <CardHeader className="pb-3 pt-4 px-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span
          className={cn('text-sm font-semibold', allFilled ? 'text-green-700' : 'text-yellow-700')}
        >
          Manufacturer Acknowledgement
        </span>
      </div>
      <Badge
        variant={allFilled ? 'default' : 'secondary'}
        className={cn(
          'text-xs flex gap-1',
          allFilled
            ? 'bg-green-600 hover:bg-green-600'
            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-100',
        )}
      >
        {allFilled ? (
          <CheckCircle2 className="h-3 w-3" />
        ) : (
          <Loader2 className="h-3 w-3 animate-spin" />
        )}
        {allFilled ? 'Received' : `${filledCount}/${totalCount}`}
      </Badge>
    </div>
  </CardHeader>
);

const SubmissionInfo: React.FC<{
  caseId: string;
  allFilled: boolean;
  workflowCompletedAt?: string;
}> = ({ caseId, allFilled, workflowCompletedAt }) => {
  const { manufacturerEmail, replyEmail, isLoading } = useManufacturerEmails(caseId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 text-xs px-4 pb-3">
        <div className="h-4 w-48 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (!manufacturerEmail && !replyEmail) return null;

  const submittedTo =
    manufacturerEmail?.to_addresses && manufacturerEmail.to_addresses.length > 0
      ? manufacturerEmail.to_addresses.join(', ')
      : null;
  const submittedDate = workflowCompletedAt || manufacturerEmail?.created_at;
  const hasSubmittedDate = !!submittedDate;
  const hasReceivedDate = !!replyEmail?.created_at;

  if (!hasSubmittedDate && !submittedTo && !hasReceivedDate) return null;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 text-xs px-4 pb-3',
        allFilled ? 'text-green-700' : 'text-yellow-700',
      )}
    >
      {hasSubmittedDate && (
        <div className="flex items-center gap-1.5">
          <span className="font-medium">Submitted Date:</span>
          <FormattedDateRelative value={submittedDate} />
        </div>
      )}
      {submittedTo && (
        <div className="flex items-center gap-1.5">
          <span className="font-medium">Submitted To:</span>
          <span className="truncate max-w-[200px]" title={submittedTo}>
            {submittedTo}
          </span>
        </div>
      )}
      {allFilled && hasReceivedDate && (
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-3 w-3" />
          <span>
            Auto-filled from email received <FormattedDateRelative value={replyEmail?.created_at} />
          </span>
        </div>
      )}
    </div>
  );
};

const PortalSubmissionInfo: React.FC<{
  caseId: string;
  allFilled: boolean;
}> = ({ caseId, allFilled }) => {
  const { data: tasksData, isLoading } = useGenericTasks(caseId, {
    type: GenericTaskType.GENERIC_TASK_TYPE_PQC_PORTAL_FILLING_TASK,
    limit: 5,
  });

  const completedTask = useMemo(
    () =>
      tasksData?.tasks?.find(
        t => t.status === GenericTaskStatus.GENERIC_TASK_STATUS_COMPLETED && t.output?.submitted,
      ),
    [tasksData],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 text-xs px-4 pb-3">
        <div className="h-4 w-48 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (!completedTask) return null;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 text-xs px-4 pb-3',
        allFilled ? 'text-green-700' : 'text-yellow-700',
      )}
    >
      <div className="flex items-center gap-1.5">
        <Globe className="h-3 w-3" />
        <span>
          Submitted via portal{' '}
          {completedTask.updated_at && <FormattedDateRelative value={completedTask.updated_at} />}
        </span>
      </div>
    </div>
  );
};

const DECISION_LABELS: Record<string, { label: string; icon: React.ElementType }> = {
  [PqcDecision.ACKNOWLEDGED]: { label: 'Manufacturer acknowledged', icon: CheckCircle2 },
  [PqcDecision.QUESTION_ASKED]: { label: 'Manufacturer asked a question', icon: HelpCircle },
  [PqcDecision.MISSING_INFO]: {
    label: 'Manufacturer requested additional information',
    icon: AlertCircle,
  },
};

const DecisionBanner: React.FC<{ decision?: string }> = ({ decision }) => {
  if (!decision || !(decision in DECISION_LABELS)) return null;
  const { label, icon: Icon } = DECISION_LABELS[decision];
  return (
    <div className="mx-4 mb-3 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span>{label}</span>
    </div>
  );
};

const AckField: React.FC<{
  field: FormField;
  value: string;
  hasValue: boolean;
  emptyPlaceholder: string;
  editableState: EditableFieldState;
  canEdit: boolean;
}> = ({ field, value, hasValue, emptyPlaceholder, editableState, canEdit }) => {
  const isEditing = editableState.editingFields[field.id];

  return (
    <div className="group">
      <div
        className={cn(
          'text-sm font-medium mb-1.5',
          hasValue ? 'text-green-700' : 'text-yellow-700',
        )}
      >
        {field.label}
      </div>
      <div
        className={cn(
          'rounded-md border p-2',
          hasValue ? 'border-green-200 bg-white' : 'border-dashed',
        )}
      >
        <div className="flex items-center justify-between">
          <EditableField
            label=""
            fieldPath={field.id}
            value={value || (hasValue ? '' : emptyPlaceholder)}
            field={field}
            editableState={editableState}
            className={{
              container: 'flex-1',
              label: 'hidden',
              value: cn(
                'text-sm',
                hasValue ? 'font-mono font-semibold text-green-700' : 'text-yellow-700 italic',
              ),
              editButton: 'hidden',
              nonEditingLayout: 'flex items-center gap-2',
            }}
          />
          {!isEditing && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => editableState.onEditStart(field.id, value)}
                  className="p-1.5 rounded text-muted-foreground hover:bg-muted"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
              )}
              {hasValue && <CopyButton value={value} label={field.label} />}
            </div>
          )}
        </div>
      </div>
      {field.description && (
        <p className={cn('mt-1 text-xs', hasValue ? 'text-green-700' : 'text-yellow-700')}>
          {field.description}
        </p>
      )}
    </div>
  );
};

const useOutputEditableState = (caseId: string): EditableFieldState => {
  const [editingFields, setEditingFields] = useState<Record<string, boolean>>({});
  const [editedValues, setEditedValues] = useState<
    Record<string, string | boolean | number | string[]>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const executeAction = useExecuteAction();

  const onEditStart = useCallback((fieldPath: string, currentValue: string | string[]) => {
    setEditingFields({ [fieldPath]: true });
    setEditedValues({ [fieldPath]: currentValue });
  }, []);

  const onEditCancel = useCallback(() => {
    setEditingFields({});
    setEditedValues({});
    setFieldErrors({});
  }, []);

  const onEditSave = useCallback(
    async (fieldPath: string) => {
      const newValue = editedValues[fieldPath];
      setIsSaving(true);

      try {
        // Route all output field updates through hermes/PQCAgent pipeline
        await new Promise((resolve, reject) => {
          executeAction.mutate(
            {
              caseId,
              action: Action.ACTION_PQC_UPDATE_OUTPUT,
              actionDataParams: {
                output_fields: { [fieldPath]: String(newValue) },
              },
            },
            { onSuccess: resolve, onError: reject },
          );
        });

        setEditingFields({});
        setEditedValues({});
        toast.success('Updated successfully');
      } catch (error: any) {
        toast.error(error?.message || 'Failed to update');
      } finally {
        setIsSaving(false);
      }
    },
    [caseId, editedValues, executeAction],
  );

  const onValueChange = useCallback(
    (fieldPath: string, value: string | boolean | number | string[]) => {
      setEditedValues(prev => ({ ...prev, [fieldPath]: value }));
    },
    [],
  );

  const onErrorClear = useCallback((fieldPath: string) => {
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[fieldPath];
      return next;
    });
  }, []);

  return {
    editingFields,
    editedValues,
    fieldErrors,
    isSaving,
    onEditStart,
    onEditCancel,
    onEditSave,
    onValueChange,
    onErrorClear,
  };
};

/**
 * Displays manufacturer acknowledgement data with inline editing support.
 * Shows pending state when awaiting response, success state when data is received.
 */
export const ManufacturerAckSection: React.FC<{
  caseId: string;
  outputGroups: FieldGroup[];
  outputData: Record<string, unknown>;
  canEdit?: boolean;
  isPortalVariant?: boolean;
  workflowCompletedAt?: string;
}> = ({
  caseId,
  outputGroups,
  outputData,
  canEdit = false,
  isPortalVariant = false,
  workflowCompletedAt,
}) => {
  const fields = useMemo(() => outputGroups.flatMap(g => g.fields), [outputGroups]);

  const { filledCount, totalCount, allFilled } = useMemo(() => {
    const total = fields.length;
    const filled = fields.filter(f => outputData[f.id] != null && outputData[f.id] !== '').length;
    return { filledCount: filled, totalCount: total, allFilled: filled === total && total > 0 };
  }, [fields, outputData]);

  const editableState = useOutputEditableState(caseId);

  // A portal submission is proven by its confirmation screenshot. Detect it so
  // the ack-id placeholder can say "this portal issues no ack id" instead of
  // "waiting for manufacturer email" — ack-less portals (e.g. FDA MedWatch)
  // would otherwise wait forever. Only fetched for portal cases.
  const { data: portalAssets } = useCaseRecordAssets(
    isPortalVariant ? caseId : null,
    AssetType.ASSET_TYPE_CALL_DOCUMENT,
    { stopPollingWhenFound: true, isFound: () => true },
  );
  const hasPortalConfirmation =
    isPortalVariant &&
    !!portalAssets?.some(
      a =>
        a.asset_name?.includes(PORTAL_CONFIRMATION_ASSET_BASE) ||
        a.file_path?.includes(PORTAL_CONFIRMATION_ASSET_BASE),
    );

  if (fields.length === 0) return null;

  return (
    <Card
      className={cn(
        'mb-4 transition-colors',
        allFilled ? 'border-green-200 bg-green-50/50' : 'border-yellow-200 bg-yellow-50/50',
      )}
    >
      <SectionHeader allFilled={allFilled} filledCount={filledCount} totalCount={totalCount} />
      <DecisionBanner decision={outputData.decision as string | undefined} />

      <CardContent className="px-4 pb-4 pt-0">
        <div className="space-y-3">
          {fields.map(field => {
            const rawValue = outputData[field.id];
            const hasValue = rawValue != null && rawValue !== '';

            return (
              <AckField
                key={field.id}
                field={field}
                value={hasValue ? String(rawValue) : ''}
                hasValue={hasValue}
                emptyPlaceholder={ackFieldPlaceholder(field.id, hasPortalConfirmation)}
                editableState={editableState}
                canEdit={canEdit}
              />
            );
          })}
        </div>
      </CardContent>
      {isPortalVariant ? (
        <PortalSubmissionInfo caseId={caseId} allFilled={allFilled} />
      ) : (
        <SubmissionInfo
          caseId={caseId}
          allFilled={allFilled}
          workflowCompletedAt={workflowCompletedAt}
        />
      )}
    </Card>
  );
};
